import { ChargebeeCheckoutItem, ChargebeeEstimate, ChargebeeEstimateRequest, ChargebeeHostCheckoutExistingRequest, ChargebeeHostCheckoutNewRequest, ChargebeeHostPortalRequest, ChargebeeHostPortalResponse, ChargebeeHostResponse, EstimateItemSummary, EstimateSummary, LineItemDiscount, getPriceFromItemId } from '../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization, post, setGoogleUserId, throwHttpError } from './api/api';
import { getFromLocalStorage, isDefined, undefinedIfEmpty } from './utils';

import accountInfo from './state/account-info';
import { getMainPage } from './page-name';
import { getStoredReferralCode } from './api/referral-api';
import organizationInfo from './state/organization-info';
import subscriptionInfo from './state/subscription-info';
import topicData from './state/topic-data';
import { ensureChargebee } from './vendor-scripts';

// Handles Checking Out a Subscription (Not one time payment) - will replace existing subscription if it exists
export async function checkoutSubscription(
    purchase_items: ChargebeeCheckoutItem[],
    callback: (error: Error | null, result: any) => void
) {
    await ensureChargebee();
    (window as any).Chargebee.init({ site: 'proximaps'});
    const cbInstance = (window as any).Chargebee.getInstance();
    const existingActiveSubscriptionId = subscriptionInfo?.subscription?.status === 'cancelled' ? undefined : undefinedIfEmpty(subscriptionInfo?.subscription_id);
    const cancelledSubscriptionId = subscriptionInfo?.subscription?.status === 'cancelled' ? undefinedIfEmpty(subscriptionInfo?.subscription_id) : undefined;
    const hasSubscriptionOnFile = isDefined(existingActiveSubscriptionId) || isDefined(cancelledSubscriptionId);
    cbInstance.openCheckout({
        hostedPage: async function () {
            // if the user has a subscription_customer_id and subscription_plan_id, then we want to replace the subscription
            if (hasSubscriptionOnFile) {
                logWithGoogleTagAndTopicOrganization('Chargebee', GoogleTagEvent.CHECKOUT_WITH_EXISTING_SUBSCRIPTION_CLICK, {
                    purchase_items: purchase_items.join(',')}
                );
                // if this is a reactivation, get the price amount for each purchase item so we dont use the old, cheaper prices
                if (isDefined(cancelledSubscriptionId)) {
                    const purchaseItemsWithPrices: ChargebeeCheckoutItem[] = [];
                    for (const item of purchase_items) {
                        const priceInfo = getPriceFromItemId(item.item_price_id);
                        if (isDefined(priceInfo)) {
                            purchaseItemsWithPrices.push({
                                item_price_id: item.item_price_id,
                                price_in_cents: priceInfo.price_in_cents,
                                quantity: item.quantity,
                            });
                        } else {
                            // Use original item if we can't determine pricing
                            purchaseItemsWithPrices.push(item);
                        }
                    }
                    purchase_items = purchaseItemsWithPrices;
                }
                const request: ChargebeeHostCheckoutExistingRequest = {
                    customer_id: subscriptionInfo.subscription_customer_id ?? 'Unknown',
                    existing_subscription_id: subscriptionInfo.subscription_id ?? 'Unknown',
                    purchase_items,
                    replace_items: true,
                    organization_id: organizationInfo.id?.$oid ?? getFromLocalStorage('organization_id') ?? undefined,
                    is_reactivation: !!isDefined(cancelledSubscriptionId),
                    referral_code: getStoredReferralCode() ?? undefined,
                };
                const response = await createChargebeeCheckoutExisting(request);

                // add checkout url to local storage so we can open it for mobile
                localStorage.setItem('checkout_url', response.hosted_page.url);
                callback(null, 'urlSet');
                return response.hosted_page;
            } else {
                let email: string | undefined =
                    accountInfo.email ?? getFromLocalStorage('email') ?? topicData.email;
                if (email === '' || email === null) {
                    email = undefined;
                }

                await setGoogleUserId(email ?? '', accountInfo.account?._id.$oid);
                logWithGoogleTagAndTopicOrganization('Chargebee', GoogleTagEvent.CHECKOUT_WITH_NEW_SUBSCRIPTION_CLICK, {
                    purchase_items: purchase_items.join(',')}
                );

                const request: ChargebeeHostCheckoutNewRequest = {
                    email,
                    purchase_items,
                    organization_id: organizationInfo.id?.$oid ?? getFromLocalStorage('organization_id') ?? undefined,
                    referral_code: getStoredReferralCode() ?? undefined,
                };
                const response = await createChargebeeCheckoutNew(request);

                // add checkout url to local storage so we can open it for mobile
                localStorage.setItem('checkout_url', response.hosted_page.url);
                callback(null, 'urlSet');
                return response.hosted_page;
            }
        },
        close: function () {
            logWithAmplitude('Chargebee Subscription Checkout', 'Subscription Checkout Closed');
            logWithGoogleTagAndTopicOrganization('Chargebee', GoogleTagEvent.CHECKOUT_ABANDONED, {
                purchase_items: purchase_items.join(',')}
            );
            callback(null, 'closed');
        },
        success: function (hostedPageId: any) {
            logWithAmplitude('Chargebee Subscription Checkout', 'Subscription Checkout Success');
            if (hasSubscriptionOnFile) {
                logWithGoogleTagAndTopicOrganization('Chargebee', GoogleTagEvent.CHECKOUT_WITH_EXISTING_SUCCESS, {
                    purchase_items: purchase_items.join(',')}
                );
            } else {
                logWithGoogleTagAndTopicOrganization('Chargebee', GoogleTagEvent.CHECKOUT_WITH_NEW_SUCCESS, {
                    purchase_items: purchase_items.join(',')}
                );
            }
            callback(null, 'success');
        },
        step: function (value: any) {
            logWithAmplitude(
                'Chargebee Subscription Checkout',
                'Subscription Checkout Step: ' + (value as string)
            );
        }
    });
}

// portal doesnt support in app. open in the page with the callback url
export async function openChargebeePortal(request: ChargebeeHostPortalRequest,) {
    try {
        const chargebeeData: ChargebeeHostPortalResponse = await createChargebeePortal(request);
        window.open(chargebeeData.portal_session.access_url);
    } catch (error) {
        getMainPage().errorToastElement?.showToastAndThrow('An Error Occurred - Unable to open subscription portal. Reach out to help.proxi.co');
    }
}


// Create Hosted Pages

export async function createChargebeeCheckoutNew(request: ChargebeeHostCheckoutNewRequest) {
    const response = await fetch(`/api/chargebee_checkout_hosted_page_new`, post(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while hosting the chargebee.', response);
    }

    return await (response.json() as Promise<ChargebeeHostResponse>);
}

export async function createChargebeeCheckoutExisting(request: ChargebeeHostCheckoutExistingRequest) {
    const response = await fetch(`/api/chargebee_checkout_hosted_page_existing`, post(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while hosting the chargebee.', response);
    }

    return await (response.json() as Promise<ChargebeeHostResponse>);
}

export async function createChargebeePortal(request: ChargebeeHostPortalRequest) {
    const response = await fetch(`/api/chargebee_portal_hosted_page`, post(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while hosting the chargebee.', response);
    }

    return await (response.json() as Promise<ChargebeeHostPortalResponse>);
}

export async function chargebeePortalClick(amplitude_callsite: string) {
    logWithAmplitude(amplitude_callsite, 'Manage Subscription Click');
    if (!isDefined(subscriptionInfo.subscription_customer_id)) {
        getMainPage().errorToastElement?.showToastAndThrow('An Error Occured - Unable to open subscription portal. Reach out to help.proxi.co');
    } else {
        const request = {
            customer_id: subscriptionInfo.subscription_customer_id,
            redirect_url: window.location.href ?? 'https://map.proxi.co/#home',
        };
        try {
            getMainPage().errorToastElement?.showToast('Opening subscription portal in new tab... if it does not open, check that you are not blocking popups for this site. Reach out to help.proxi.co if you have any issues.');
            await openChargebeePortal(request);
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow('An Error Occured - Unable to open subscription portal. Reach out to help.proxi.co');
        }
    }
}

export async function getChargebeeEstimate(existing_subscription?: string,  purchase_items?: ChargebeeCheckoutItem[], customer_id?: string, is_reactivation: boolean = false) {
    const request: ChargebeeEstimateRequest = {
        existing_subscription_id : existing_subscription,
        purchase_items,
        is_reactivation,
        customer_id
    };
    const response = await fetch(`/api/chargebee_estimate`, post(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while estimating the chargebee.', response);
    }

    // process response
    const summary = await (response.json() as Promise<ChargebeeEstimate>);
    return processEstimate(summary);
}

export function processEstimate(estimate: ChargebeeEstimate) {
    const nowInvoiceEstimateElement = estimate.invoice_estimate;
    const items: EstimateItemSummary[] | undefined = nowInvoiceEstimateElement?.line_items.map((item): EstimateItemSummary => {
        return {
            pretty_name: item.description,
            amount: item.amount,
            discount_amount: item.discount_amount + item.item_level_discount_amount,
            plan_id: item.entity_id,
            quantity: item.quantity ?? 1,
        };
    });

    const purchase_object: ChargebeeCheckoutItem[] | undefined = nowInvoiceEstimateElement?.line_items.map((item): ChargebeeCheckoutItem => {
        return {
            item_price_id: item.entity_id,
            quantity: item.quantity ?? 1,
        };
    }
    );

    const summary: EstimateSummary = {
        due_today: nowInvoiceEstimateElement?.amount_due ?? 0,
        due_next: nowInvoiceEstimateElement?.total ?? 0,
        recurring_cost: nowInvoiceEstimateElement?.total ?? 0,
        next_renewal_total: estimate.next_invoice_estimate?.total,
        credits: nowInvoiceEstimateElement?.credits_applied ?? 0,
        applied_discounts: nowInvoiceEstimateElement?.line_item_discounts?.map((discount: LineItemDiscount) => discount.coupon_id) ?? [],
        estimate_items: items ?? [],
        purchase_object: purchase_object ?? [],
    };

    return summary;
}
